import { supabase } from '../../lib/supabase';
import { isEventNotFinishedYet } from '../../utils/eventSchedule';

/**
 * Klasa SingleEvent - odpowiedzialna za pobieranie i zarządzanie danymi pojedynczego eventu
 */
class SingleEvent {
  constructor(eventId, eventData = null) {
    this.eventId = eventId;
    this.eventData = eventData;
  }

  /**
   * Pobiera podstawowe dane eventu
   * @returns {Promise<Object>} Dane eventu
   */
  static async loadEvent(eventId) {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('id', eventId)
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error loading event:', error);
      return { data: null, error };
    }
  }

  /**
   * Pobiera dane hosta eventu
   * @param {string} hostId - ID hosta
   * @returns {Promise<Object>} Dane hosta
   */
  static async loadHost(hostId) {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, name, nickname, first_name, last_name')
        .eq('id', hostId)
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error loading host:', error);
      return { data: null, error };
    }
  }

  /**
   * Pobiera listę uczestników eventu
   * @param {string} eventId - ID eventu
   * @returns {Promise<Object>} Lista uczestników z ich profilami
   */
  static async loadParticipants(eventId) {
    try {
      // Load active participants
      const { data: participantsData, error: fetchError } = await supabase
        .from('event_participants')
        .select('*')
        .eq('event_id', eventId)
        .eq('status', 'active')
        .order('joined_at', { ascending: true });

      if (fetchError) throw fetchError;

      // Load user profiles for participants
      if (participantsData && participantsData.length > 0) {
        const userIds = participantsData.map(p => p.user_id);
        const { data: usersData, error: usersError } = await supabase
          .from('users')
          .select('id, name, nickname, first_name, last_name, avatar_url, is_active')
          .in('id', userIds);

        if (!usersError && usersData) {
          // Map users to participants
          const participantsWithUsers = participantsData.map(participant => ({
            ...participant,
            user: usersData.find(u => u.id === participant.user_id) || null
          }));
          return { data: participantsWithUsers, error: null };
        } else {
          return { 
            data: participantsData.map(p => ({ ...p, user: null })), 
            error: usersError || null 
          };
        }
      } else {
        return { data: [], error: null };
      }
    } catch (error) {
      console.error('Error loading participants:', error);
      return { data: [], error };
    }
  }

  /**
   * Pobiera liczbę uczestników eventu
   * @param {string} eventId - ID eventu
   * @returns {Promise<number>} Liczba uczestników
   */
  static async loadParticipantsCount(eventId) {
    try {
      const { count, error } = await supabase
        .from('event_participants')
        .select('*', { count: 'exact', head: true })
        .eq('event_id', eventId)
        .eq('status', 'active');

      if (error) throw error;
      return { count: count || 0, error: null };
    } catch (error) {
      console.error('Error loading participants count:', error);
      return { count: 0, error };
    }
  }

  /**
   * Pobiera aplikację użytkownika do eventu
   * @param {string} eventId - ID eventu
   * @param {string} userId - ID użytkownika
   * @returns {Promise<Object>} Dane aplikacji
   */
  static async loadUserApplication(eventId, userId) {
    try {
      if (!userId) {
        return { data: null, error: null };
      }

      const { data, error } = await supabase
        .from('event_applications')
        .select('*')
        .eq('event_id', eventId)
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        throw error;
      }

      return { data: data || null, error: null };
    } catch (error) {
      console.error('Error loading user application:', error);
      return { data: null, error };
    }
  }

  /**
   * Pobiera listę zgłoszeń do eventu (dla hosta)
   * @param {string} eventId - ID eventu
   * @param {string} status - Status zgłoszeń (opcjonalnie, domyślnie 'pending')
   * @returns {Promise<Object>} Lista zgłoszeń z profilami użytkowników
   */
  static async loadApplications(eventId, status = 'pending') {
    try {
      // Load applications
      const { data: applicationsData, error: fetchError } = await supabase
        .from('event_applications')
        .select('*')
        .eq('event_id', eventId)
        .eq('status', status)
        .order('created_at', { ascending: true });

      if (fetchError) throw fetchError;

      // Load user profiles for applications
      if (applicationsData && applicationsData.length > 0) {
        const userIds = applicationsData.map(a => a.user_id);
        const { data: usersData, error: usersError } = await supabase
          .from('users')
          .select('id, name, nickname, first_name, last_name')
          .in('id', userIds);

        if (!usersError && usersData) {
          // Map users to applications
          const applicationsWithUsers = applicationsData.map(application => ({
            ...application,
            user: usersData.find(u => u.id === application.user_id) || null
          }));
          return { data: applicationsWithUsers, error: null };
        } else {
          return { 
            data: applicationsData.map(a => ({ ...a, user: null })), 
            error: usersError || null 
          };
        }
      } else {
        return { data: [], error: null };
      }
    } catch (error) {
      console.error('Error loading applications:', error);
      return { data: [], error };
    }
  }

  /**
   * Pobiera wiadomości z czatu eventu
   * @param {string} eventId - ID eventu
   * @param {number} limit - Limit wiadomości (domyślnie 50)
   * @returns {Promise<Object>} Lista wiadomości z profilami użytkowników
   */
  /**
   * Pobiera liczbę nieprzeczytanych wiadomości dla użytkownika
   * @param {string} eventId - ID eventu
   * @param {string} userId - ID użytkownika
   * @returns {Promise<Object>} Liczba nieprzeczytanych wiadomości
   */
  static async loadUnreadMessagesCount(eventId, userId) {
    try {
      if (!userId) {
        return { count: 0, error: null };
      }

      // Pobierz ostatni czas przeczytania wiadomości
      let lastReadAt = null;
      const { data: readData, error: readError } = await supabase
        .from('event_message_reads')
        .select('last_read_at')
        .eq('event_id', eventId)
        .eq('user_id', userId)
        .maybeSingle();

      // Jeśli tabela nie istnieje (404) lub wystąpił inny błąd, traktuj jak brak przeczytanych wiadomości
      if (readError) {
        // 404 oznacza że tabela nie istnieje - to jest OK, po prostu nie ma jeszcze przeczytanych wiadomości
        if (readError.code === 'PGRST116' || readError.message?.includes('404')) {
          console.warn('Table event_message_reads does not exist yet. Run migration 015-create-event-message-reads-table.sql');
          lastReadAt = null;
        } else {
          // Inny błąd - loguj ale kontynuuj
          console.warn('Error loading read status:', readError);
          lastReadAt = null;
        }
      } else {
        lastReadAt = readData?.last_read_at || null;
      }

      // Pobierz liczbę wiadomości utworzonych po ostatnim przeczytaniu
      let query = supabase
        .from('event_messages')
        .select('id', { count: 'exact', head: true })
        .eq('event_id', eventId);

      // Jeśli użytkownik nie przeczytał jeszcze żadnej wiadomości, policz wszystkie
      // W przeciwnym razie policz tylko te utworzone po ostatnim przeczytaniu
      if (lastReadAt) {
        query = query.gt('created_at', lastReadAt);
      }

      // Wyklucz wiadomości wysłane przez samego użytkownika
      query = query.neq('user_id', userId);

      const { count, error } = await query;

      if (error) throw error;
      return { count: count || 0, error: null };
    } catch (error) {
      console.error('Error loading unread messages count:', error);
      return { count: 0, error };
    }
  }

  /**
   * Oznacza wszystkie wiadomości w evencie jako przeczytane dla użytkownika
   * @param {string} eventId - ID eventu
   * @param {string} userId - ID użytkownika
   * @returns {Promise<Object>} Wynik operacji
   */
  static async markMessagesAsRead(eventId, userId) {
    try {
      if (!userId) {
        return { error: null };
      }

      // Upsert - aktualizuj jeśli istnieje, utwórz jeśli nie
      const { error } = await supabase
        .from('event_message_reads')
        .upsert({
          event_id: eventId,
          user_id: userId,
          last_read_at: new Date().toISOString()
        }, {
          onConflict: 'event_id,user_id'
        });

      // Jeśli tabela nie istnieje (404), to nie jest krytyczny błąd
      if (error) {
        if (error.code === 'PGRST116' || error.message?.includes('404')) {
          console.warn('Table event_message_reads does not exist yet. Run migration 015-create-event-message-reads-table.sql');
          // Nie rzucamy błędu - funkcjonalność działa bez tej tabeli (po prostu nie śledzi przeczytanych wiadomości)
          return { error: null };
        }
        throw error;
      }
      return { error: null };
    } catch (error) {
      console.error('Error marking messages as read:', error);
      // Jeśli to błąd 404, nie traktuj go jako krytyczny
      if (error.code === 'PGRST116' || error.message?.includes('404')) {
        return { error: null };
      }
      return { error };
    }
  }

  /**
   * Pobiera wiadomości z czatu eventu z paginacją
   * @param {string} eventId - ID eventu
   * @param {number} limit - Limit wiadomości (domyślnie 30)
   * @param {string} beforeId - ID wiadomości przed którą ładujemy (dla paginacji)
   * @returns {Promise<Object>} Lista wiadomości z profilami użytkowników i informacją o hasMore
   */
  static async loadMessages(eventId, limit = 30, beforeId = null) {
    try {
      let query = supabase
        .from('event_messages')
        .select('*')
        .eq('event_id', eventId)
        .order('created_at', { ascending: false })
        .limit(limit + 1); // Pobierz 1 więcej żeby sprawdzić czy są jeszcze starsze

      // Jeśli mamy beforeId, pobierz wiadomości starsze od tej
      if (beforeId) {
        // Najpierw pobierz created_at dla beforeId
        const { data: beforeMsg } = await supabase
          .from('event_messages')
          .select('created_at')
          .eq('id', beforeId)
          .single();
        
        if (beforeMsg) {
          query = query.lt('created_at', beforeMsg.created_at);
        }
      }

      const { data: messagesData, error } = await query;

      if (error) throw error;

      // Sprawdź czy są jeszcze starsze wiadomości
      const hasMore = messagesData && messagesData.length > limit;
      
      // Usuń dodatkową wiadomość jeśli była pobrana
      const messages = hasMore ? messagesData.slice(0, limit) : (messagesData || []);
      
      // Odwróć kolejność żeby najstarsze były na górze
      messages.reverse();

      // Load user profiles for messages (tylko dla wiadomości użytkowników, nie systemowych)
      if (messages.length > 0) {
        const userIds = [...new Set(messages.filter(m => !m.is_system && m.user_id).map(m => m.user_id))];
        
        if (userIds.length > 0) {
          const { data: usersData, error: usersError } = await supabase
            .from('users')
            .select('id, name, nickname, first_name, last_name')
            .in('id', userIds);

          if (!usersError && usersData) {
            // Map users to messages
            const messagesWithUsers = messages.map(message => ({
              ...message,
              users: message.is_system || !message.user_id ? null : (usersData.find(u => u.id === message.user_id) || null)
            }));
            return { data: messagesWithUsers, hasMore, error: null };
          } else {
            return { 
              data: messages.map(m => ({ ...m, users: null })), 
              hasMore,
              error: usersError || null 
            };
          }
        } else {
          // Brak wiadomości użytkowników, tylko systemowe
          return { data: messages.map(m => ({ ...m, users: null })), hasMore, error: null };
        }
      } else {
        return { data: [], hasMore: false, error: null };
      }
    } catch (error) {
      console.error('Error loading messages:', error);
      return { data: [], hasMore: false, error };
    }
  }

  /**
   * Tworzy wiadomość systemową w czacie eventu
   * @param {string} eventId - ID eventu
   * @param {string} message - Treść wiadomości systemowej
   * @returns {Promise<Object>} Wynik operacji
   */
  static async createSystemMessage(eventId, message) {
    try {
      if (!message || !message.trim()) {
        throw new Error('System message cannot be empty');
      }

      const { error } = await supabase
        .from('event_messages')
        .insert({
          event_id: eventId,
          user_id: null, // Wiadomości systemowe nie mają user_id
          message: message.trim(),
          is_system: true
        });

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error creating system message:', error);
      return { error };
    }
  }

  /**
   * Formatuje wiadomość systemową o aktualizacji wydarzenia
   * @param {Array<string>} changes - Lista zmienionych pól (np. ['title', 'date', 'startTime'])
   * @param {Function} t - Funkcja tłumaczenia
   * @returns {string} Sformatowana wiadomość systemowa
   */
  static formatUpdateSystemMessage(changes, t) {
    if (!changes || changes.length === 0) {
      return t('events.chat.system.eventUpdated', 'Host zaktualizował wydarzenie');
    }

    const changeLabels = changes.map(change => {
      switch (change) {
        case 'title':
          return t('events.chat.system.fields.title', 'nazwę');
        case 'description':
          return t('events.chat.system.fields.description', 'opis');
        case 'date':
          return t('events.chat.system.fields.date', 'datę');
        case 'startTime':
          return t('events.chat.system.fields.startTime', 'godzinę rozpoczęcia');
        case 'duration':
          return t('events.chat.system.fields.duration', 'długość wydarzenia');
        case 'location':
          return t('events.chat.system.fields.location', 'adres');
        case 'banner':
          return t('events.chat.system.fields.banner', 'baner wydarzenia');
        case 'maxParticipants':
          return t('events.chat.system.fields.maxParticipants', 'liczbę miejsc');
        case 'isFree':
          return t('events.chat.system.fields.isFree', 'informację o płatności');
        case 'totalCost':
          return t('events.chat.system.fields.totalCost', 'koszt');
        case 'additionalInstructions':
          return t('events.chat.system.fields.additionalInstructions', 'dodatkowe instrukcje');
        case 'eventType':
          return t('events.chat.system.fields.eventType', 'typ wydarzenia');
        default:
          return change;
      }
    });

    if (changeLabels.length === 1) {
      return t('events.chat.system.eventFieldUpdated', {
        field: changeLabels[0],
        defaultValue: `Host zaktualizował ${changeLabels[0]} wydarzenia`
      });
    } else {
      const lastField = changeLabels.pop();
      const fieldsList = changeLabels.join(', ') + ' i ' + lastField;
      return t('events.chat.system.eventFieldsUpdated', {
        fields: fieldsList,
        defaultValue: `Host zaktualizował ${fieldsList} wydarzenia`
      });
    }
  }

  /**
   * Aktualizuje dane wydarzenia (tylko dla hosta)
   * @param {string} eventId - ID eventu
   * @param {string} hostId - ID hosta (do weryfikacji)
   * @param {Object} updateData - Dane do aktualizacji
   * @returns {Promise<Object>} Wynik operacji
   */
  static async updateEvent(eventId, hostId, updateData) {
    try {
      // Sprawdź czy wydarzenie istnieje i czy użytkownik jest hostem
      const eventResult = await SingleEvent.loadEvent(eventId);
      if (eventResult.error || !eventResult.data) {
        return { success: false, error: eventResult.error || new Error('Event not found') };
      }

      const event = eventResult.data;
      if (event.host_id !== hostId) {
        return { success: false, error: new Error('Only the host can update the event') };
      }

      if (!isEventNotFinishedYet(event)) {
        return { success: false, error: new Error('Finished events cannot be edited') };
      }

      // Aktualizuj wydarzenie
      const { data, error: updateError } = await supabase
        .from('events')
        .update(updateData)
        .eq('id', eventId)
        .eq('host_id', hostId)
        .select()
        .single();

      if (updateError) throw updateError;

      return { success: true, data, error: null };
    } catch (error) {
      console.error('Error updating event:', error);
      return { success: false, error };
    }
  }

  /**
   * Anuluje wydarzenie (tylko dla hosta)
   * @param {string} eventId - ID eventu
   * @param {string} hostId - ID hosta (do weryfikacji)
   * @param {Function} t - Funkcja tłumaczenia (opcjonalnie)
   * @returns {Promise<Object>} Wynik operacji
   */
  static async cancelEvent(eventId, hostId, t = null) {
    try {
      // Sprawdź czy wydarzenie istnieje i czy użytkownik jest hostem
      const eventResult = await SingleEvent.loadEvent(eventId);
      if (eventResult.error || !eventResult.data) {
        return { success: false, error: eventResult.error || new Error('Event not found') };
      }

      const event = eventResult.data;
      if (event.host_id !== hostId) {
        return { success: false, error: new Error('Only the host can cancel the event') };
      }

      if (event.is_cancelled) {
        return { success: false, error: new Error('Event is already cancelled') };
      }

      // Ustaw is_cancelled = true
      const { error: updateError } = await supabase
        .from('events')
        .update({ is_cancelled: true })
        .eq('id', eventId)
        .eq('host_id', hostId);

      if (updateError) throw updateError;

      // Pobierz wszystkich uczestników
      const participantsResult = await SingleEvent.loadParticipants(eventId);
      const participants = participantsResult.data || [];

      // Utwórz wiadomość systemową w czacie
      const cancelMessage = t 
        ? t('events.chat.system.eventCancelledByHost')
        : 'Wydarzenie zostało anulowane przez organizatora';
      const systemMessageResult = await SingleEvent.createSystemMessage(eventId, cancelMessage);
      if (systemMessageResult.error) {
        console.error('Error creating system message:', systemMessageResult.error);
        // Nie przerywamy - kontynuujemy mimo błędu wiadomości
      }

      // Pobierz użytkowników z oczekującymi zgłoszeniami
      const applicationsResult = await SingleEvent.loadApplications(eventId, 'pending');
      const pendingApplications = applicationsResult.data || [];

      // Utwórz powiadomienia dla wszystkich uczestników
      const participantNotificationPromises = participants.map(async (participant) => {
        try {
          const { error } = await supabase.rpc('create_notification', {
            p_user_id: participant.user_id,
            p_type: 'event_cancelled',
            p_data: {
              event_title: event.title
            },
            p_event_id: eventId,
            p_application_id: null
          });
          if (error) {
            console.error(`Error creating notification for participant ${participant.user_id}:`, error);
          }
          return { success: !error, error };
        } catch (err) {
          console.error(`Error creating notification for participant ${participant.user_id}:`, err);
          return { success: false, error: err };
        }
      });

      // Utwórz powiadomienia dla użytkowników z oczekującymi zgłoszeniami
      const pendingNotificationPromises = pendingApplications.map(async (application) => {
        try {
          const { error } = await supabase.rpc('create_notification', {
            p_user_id: application.user_id,
            p_type: 'event_cancelled',
            p_data: {
              event_title: event.title
            },
            p_event_id: eventId,
            p_application_id: application.id
          });
          if (error) {
            console.error(`Error creating notification for pending user ${application.user_id}:`, error);
          }
          return { success: !error, error };
        } catch (err) {
          console.error(`Error creating notification for pending user ${application.user_id}:`, err);
          return { success: false, error: err };
        }
      });

      // Wykonaj wszystkie powiadomienia równolegle (nie czekamy na wszystkie)
      await Promise.allSettled([...participantNotificationPromises, ...pendingNotificationPromises]);

      return { success: true, error: null };
    } catch (error) {
      console.error('Error cancelling event:', error);
      return { success: false, error };
    }
  }

  /**
   * Pobiera wszystkie dane eventu na raz (event, host, participants, userApplication, messages)
   * @param {string} eventId - ID eventu
   * @param {string} userId - ID użytkownika (opcjonalnie)
   * @returns {Promise<Object>} Wszystkie dane eventu
   */
  static async loadAll(eventId, userId = null) {
    try {
      // Get current user if not provided
      if (!userId) {
        const { data: { user } } = await supabase.auth.getUser();
        userId = user?.id || null;
      }

      // Load all data in parallel
      const [eventResult, participantsResult, participantsCountResult, userApplicationResult] = await Promise.all([
        SingleEvent.loadEvent(eventId),
        SingleEvent.loadParticipants(eventId),
        SingleEvent.loadParticipantsCount(eventId),
        userId ? SingleEvent.loadUserApplication(eventId, userId) : Promise.resolve({ data: null, error: null })
      ]);

      // Load host if event data is available
      let hostResult = { data: null, error: null };
      if (eventResult.data && eventResult.data.host_id) {
        hostResult = await SingleEvent.loadHost(eventResult.data.host_id);
      }

      return {
        event: eventResult.data,
        host: hostResult.data,
        participants: participantsResult.data || [],
        participantsCount: participantsCountResult.count || 0,
        userApplication: userApplicationResult.data,
        errors: {
          event: eventResult.error,
          host: hostResult.error,
          participants: participantsResult.error,
          participantsCount: participantsCountResult.error,
          userApplication: userApplicationResult.error
        }
      };
    } catch (error) {
      console.error('Error loading all event data:', error);
      return {
        event: null,
        host: null,
        participants: [],
        participantsCount: 0,
        userApplication: null,
        errors: { general: error }
      };
    }
  }

  /**
   * Pobiera dane eventu i tworzy instancję klasy
   * @param {string} eventId - ID eventu
   * @returns {Promise<SingleEvent>} Instancja klasy z danymi
   */
  static async create(eventId) {
    const { data, error } = await SingleEvent.loadEvent(eventId);
    if (error) {
      throw error;
    }
    return new SingleEvent(eventId, data);
  }

  /**
   * Metody instancyjne - działają na danych już załadowanych
   */

  /**
   * Sprawdza czy użytkownik jest hostem
   * @param {string} userId - ID użytkownika
   * @returns {boolean}
   */
  isHost(userId) {
    return this.eventData && this.eventData.host_id === userId;
  }

  /**
   * Sprawdza czy użytkownik jest uczestnikiem (na podstawie statusu zgłoszenia)
   * @deprecated Użyj isParticipantInDB() dla dokładniejszego sprawdzenia
   * @param {Object} userApplication - Aplikacja użytkownika
   * @returns {boolean}
   */
  isParticipant(userApplication) {
    return userApplication?.status === 'approved';
  }

  /**
   * Sprawdza czy użytkownik jest faktycznie uczestnikiem (w bazie danych)
   * @param {string} userId - ID użytkownika
   * @returns {Promise<boolean>}
   */
  static async isParticipantInDB(eventId, userId) {
    try {
      if (!userId || !eventId) return false;

      const { data, error } = await supabase
        .from('event_participants')
        .select('id')
        .eq('event_id', eventId)
        .eq('user_id', userId)
        .eq('status', 'active')
        .maybeSingle();

      return !error && data !== null;
    } catch (error) {
      console.error('Error checking participant status:', error);
      return false;
    }
  }

  /**
   * Sprawdza czy event jest pełny
   * @param {number} participantsCount - Liczba uczestników
   * @returns {boolean}
   */
  isFull(participantsCount) {
    return this.eventData && participantsCount >= this.eventData.max_participants;
  }

  /**
   * Sprawdza czy event się już zakończył
   * @returns {boolean}
   */
  isEnded() {
    if (!this.eventData) return false;
    const now = new Date();
    const eventDateTime = new Date(`${this.eventData.event_date}T${this.eventData.start_time}`);
    return eventDateTime < now;
  }

  /**
   * Pobiera status eventu
   * @param {number} participantsCount - Liczba uczestników
   * @returns {string} 'active' | 'full' | 'ended'
   */
  getStatus(participantsCount) {
    if (this.isEnded()) return 'ended';
    if (this.isFull(participantsCount)) return 'full';
    return 'active';
  }

  /**
   * Pobiera status wydarzenia z bazy danych (upcoming, in_progress, ended)
   * @returns {string} 'upcoming' | 'in_progress' | 'ended'
   */
  getEventStatus() {
    if (!this.eventData) return 'upcoming';
    return this.eventData.status || 'upcoming';
  }

  /**
   * Sprawdza czy wydarzenie rozpoczyna się wkrótce (w ciągu godziny, ale jeszcze nie rozpoczęte)
   * @returns {boolean}
   */
  isStartingSoon() {
    if (!this.eventData) return false;
    const now = new Date();
    const eventDateTime = new Date(`${this.eventData.event_date}T${this.eventData.start_time}`);
    const oneHourFromNow = new Date(now.getTime() + 60 * 60 * 1000);
    
    // Wydarzenie rozpoczyna się wkrótce jeśli jest między teraz a godziną od teraz
    // i jeszcze się nie rozpoczęło
    return eventDateTime > now && eventDateTime <= oneHourFromNow;
  }

  /**
   * Pobiera czytelny status wydarzenia do wyświetlenia
   * @param {Function} t - Funkcja tłumaczenia
   * @returns {string} Przetłumaczony status
   */
  getDisplayStatus(t) {
    if (!this.eventData) return t('events.status.upcoming', 'Nadchodzące');
    
    const status = this.getEventStatus();
    const isSoon = this.isStartingSoon();
    
    if (status === 'in_progress') {
      return t('events.status.inProgress', 'W trakcie');
    } else if (status === 'ended') {
      return t('events.status.ended', 'Zakończone');
    } else if (isSoon && status === 'upcoming') {
      return t('events.status.startingSoon', 'Rozpoczyna się wkrótce');
    } else {
      return t('events.status.upcoming', 'Nadchodzące');
    }
  }

  /**
   * Formatuje datę eventu
   * @returns {string} Data w formacie dd.mm.yyyy
   */
  formatDate() {
    if (!this.eventData) return '';
    const date = new Date(this.eventData.event_date);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  }

  /**
   * Formatuje czas rozpoczęcia
   * @returns {string} Czas w formacie HH:mm
   */
  formatTime() {
    if (!this.eventData) return '';
    return this.eventData.start_time ? this.eventData.start_time.substring(0, 5) : '';
  }

  /**
   * Formatuje czas trwania
   * @returns {string} Czas trwania w formacie "Xh Ymin"
   */
  formatDuration() {
    if (!this.eventData) return '';
    const minutes = this.eventData.duration_minutes;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) {
      return `${mins}min`;
    } else if (mins === 0) {
      return `${hours}h`;
    } else {
      return `${hours}h ${mins}min`;
    }
  }

  /**
   * Pobiera nazwę dnia tygodnia
   * @param {string} locale - Lokalizacja (domyślnie 'pl-PL')
   * @returns {string} Nazwa dnia tygodnia
   */
  getDayName(locale = 'pl-PL') {
    if (!this.eventData) return '';
    const date = new Date(this.eventData.event_date);
    return date.toLocaleDateString(locale, { weekday: 'long' });
  }

  /**
   * Oblicza liczbę dni do rozpoczęcia eventu
   * @returns {number} Liczba dni (może być ujemna, jeśli event już się odbył)
   */
  getDaysUntilEvent() {
    if (!this.eventData) return 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const eventDate = new Date(this.eventData.event_date);
    eventDate.setHours(0, 0, 0, 0);
    const diffTime = eventDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }

  /**
   * Pobiera typ eventu w formacie do wyświetlenia
   * @param {Function} t - Funkcja tłumaczenia (z react-i18next)
   * @returns {string} Przetłumaczony typ eventu
   */
  getEventTypeLabel(t) {
    if (!this.eventData || !t) return '';
    const eventTypes = {
      'sport': t('events.createEvent.eventTypes.sport'),
      'party': t('events.createEvent.eventTypes.party'),
      'gaming': t('events.createEvent.eventTypes.gaming'),
      'hangout': t('events.createEvent.eventTypes.hangout'),
      'trip': t('events.createEvent.eventTypes.trip'),
      'other': t('events.createEvent.eventTypes.other'),
    };
    return eventTypes[this.eventData.event_type] || this.eventData.event_type;
  }

  /**
   * Pobiera liczbę polubień eventu
   * @param {string} eventId - ID eventu
   * @returns {Promise<Object>} Liczba polubień
   */
  static async loadLikesCount(eventId) {
    try {
      const { count, error } = await supabase
        .from('event_likes')
        .select('*', { count: 'exact', head: true })
        .eq('event_id', eventId);

      if (error) throw error;
      return { count: count || 0, error: null };
    } catch (error) {
      console.error('Error loading likes count:', error);
      return { count: 0, error };
    }
  }

  /**
   * Sprawdza czy użytkownik polubił event
   * @param {string} eventId - ID eventu
   * @param {string} userId - ID użytkownika
   * @returns {Promise<boolean>} Czy użytkownik polubił event
   */
  static async isLikedByUser(eventId, userId) {
    try {
      if (!userId || !eventId) return false;

      const { data, error } = await supabase
        .from('event_likes')
        .select('id')
        .eq('event_id', eventId)
        .eq('user_id', userId)
        .maybeSingle();

      return !error && data !== null;
    } catch (error) {
      console.error('Error checking like status:', error);
      return false;
    }
  }

  /**
   * Przełącza polubienie eventu (dodaje lub usuwa)
   * @param {string} eventId - ID eventu
   * @param {string} userId - ID użytkownika
   * @returns {Promise<Object>} Wynik operacji { liked: boolean, count: number, error: null|Error }
   */
  static async toggleLike(eventId, userId) {
    try {
      if (!userId || !eventId) {
        return { liked: false, count: 0, error: new Error('User ID and Event ID are required') };
      }

      // Sprawdź czy użytkownik już polubił
      const isLiked = await SingleEvent.isLikedByUser(eventId, userId);

      if (isLiked) {
        // Usuń polubienie
        const { error } = await supabase
          .from('event_likes')
          .delete()
          .eq('event_id', eventId)
          .eq('user_id', userId);

        if (error) throw error;

        // Pobierz nową liczbę polubień
        const countResult = await SingleEvent.loadLikesCount(eventId);
        return { liked: false, count: countResult.count, error: null };
      } else {
        // Dodaj polubienie
        const { error } = await supabase
          .from('event_likes')
          .insert({
            event_id: eventId,
            user_id: userId
          });

        if (error) throw error;

        // Pobierz nową liczbę polubień
        const countResult = await SingleEvent.loadLikesCount(eventId);
        return { liked: true, count: countResult.count, error: null };
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      return { liked: false, count: 0, error };
    }
  }
}

export default SingleEvent;
